import { CheckCircle, Leaf, Heart, Shield } from 'lucide-react';
import { motion } from 'framer-motion';

export const About = () => {
  const principles = [
    {
      icon: Leaf,
      title: 'Root Cause Healing',
      description: 'We address the underlying cause of ailments, not just symptoms, for lasting wellness.'
    },
    {
      icon: Heart,
      title: 'Personalized Treatment',
      description: 'Every treatment plan is tailored to your unique body constitution (Prakriti).'
    },
    {
      icon: Shield,
      title: 'Natural & Safe',
      description: '100% natural herbs and therapies with no harmful side effects.'
    }
  ];

  const benefits = [
    'Ancient wisdom backed by modern research',
    'Experienced Ayurvedic physicians',
    'Comprehensive diagnostic approach',
    'Sustainable lifestyle guidance',
    'Follow-up care and support'
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-padding bg-[#F5F2EB]"
    >
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="caption mb-4 block">About Ayurveda</span>
            <h2 className="heading-2 text-[#2D4A3E] mb-6" data-testid="about-heading">
              The Science of Life & Longevity
            </h2>
            <p className="body-large text-[#6B6B6B] mb-6">
              Ayurveda, the 5,000-year-old science of life, offers a holistic approach
              to health that considers mind, body, and spirit as interconnected. At
              AyurvedMed Healthcare, we bring this ancient wisdom to modern healing.
            </p>
            <p className="text-[#6B6B6B] mb-8 leading-relaxed">
              Our experienced practitioners use traditional diagnostic methods like
              Nadi Pariksha (pulse diagnosis) combined with modern insights to create
              personalized treatment plans. We believe in treating the whole person,
              not just the disease.
            </p>

            {/* Benefits List */}
            <ul className="space-y-3">
              {benefits.map((benefit, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3 text-[#4A4A4A]"
                >
                  <CheckCircle className="w-5 h-5 text-[#C67D63] flex-shrink-0" />
                  {benefit}
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right Image & Cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1637665759389-41818b867562?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwY2xpbmljJTIwd2FpdGluZyUyMHJvb20lMjBwbGFudHN8ZW58MHx8fHwxNzcwMjI2MDIyfDA&ixlib=rb-4.1.0&q=85"
                alt="AyurvedMed Clinic"
                className="w-full h-[400px] object-cover"
                data-testid="about-image"
              />
            </div>

            {/* Decorative Element */}
            <div className="absolute -z-10 -bottom-6 -right-6 w-full h-full rounded-3xl border-2 border-[#C67D63]/30" />
          </motion.div>
        </div>

        {/* Principles Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-20">
          {principles.map((principle, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-300"
              data-testid={`principle-card-${index}`}
            >
              <div className="w-14 h-14 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center mb-6">
                <principle.icon className="w-7 h-7 text-[#2D4A3E]" />
              </div>
              <h3 className="heading-3 text-[#2D4A3E] mb-3">{principle.title}</h3>
              <p className="text-[#6B6B6B] leading-relaxed">{principle.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
